/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import {
  APIResponseInterface,
  TransactionEMSPInitialProperties,
} from '../../interface/emsp-transaction-interface';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import FormatUtcString from '../../shared-functions/FormatUtcString';
// import paymentApi from '../../utils/api/payments';
import transactionApi from '../../utils/api/v2/emsp-session';

import { setNotificationMessage } from './NotificationManagementSlice';

// Define the type of data expected
interface FetchParameters {
  search?: string;
  page?: number;
  limit?: number;
}
// a thunk to fetch sessions
export const fetchAllSession = createAsyncThunk<APIResponseInterface, FetchParameters>(
  'commercial-sessions-management/fetchAllSession',
  async (data: FetchParameters, { dispatch }) => {
    try {
      const [response, statusCountResponse] = await Promise.all([
        transactionApi.fetchCommercialSessionList(removeEmptyKeys(data)),
        transactionApi.fetchCommercialSessionStatusCount(removeEmptyKeys(data)).catch(() => null),
      ]);

      const statusCounts = statusCountResponse?.data || statusCountResponse || {};

      return {
        ...response.data,
        page: response.data?.page ?? response.data?.currentPage ?? data.page ?? 1,
        limit: response.data?.limit ?? data.limit ?? 10,
        TotalFinising: statusCounts.finished ?? response.data.TotalFinising ?? 0,
        totalInprocess: statusCounts.inprogress ?? statusCounts.inProgress ?? response.data.totalInprocess ?? 0,
        totalFailed: statusCounts.failed ?? response.data.totalFailed ?? 0,
        results: response.data.results.map((roleData: any) => ({
          ...roleData,
          date: ConvertUTCtoDate(roleData.createdAt),
          time: FormatUtcString(ConvertUTCtoHHMM(roleData.createdAt)),
          id: roleData._id,
        })),
      };
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message,
          code: error?.response?.data?.code,
          status: true,
        }),
      );
      throw error;
    }
  },
);

// a thunk to refresh sessions
export const refreshAllSessions = createAsyncThunk<APIResponseInterface, FetchParameters>(
  'commercial-sessions-management/refreshAllSessions',
  async (data: FetchParameters) => {
    const [response, statusCountResponse] = await Promise.all([
      transactionApi.fetchCommercialSessionList(removeEmptyKeys(data)),
      transactionApi.fetchCommercialSessionStatusCount(removeEmptyKeys(data)).catch(() => null),
    ]);

    const statusCounts = statusCountResponse?.data || statusCountResponse || {};

    return {
      ...response.data,
      page: response.data?.page ?? response.data?.currentPage ?? data.page ?? 1,
      limit: response.data?.limit ?? data.limit ?? 10,
      TotalFinising: statusCounts.finished ?? response.data.TotalFinising ?? 0,
      totalInprocess: statusCounts.inprogress ?? statusCounts.inProgress ?? response.data.totalInprocess ?? 0,
      totalFailed: statusCounts.failed ?? response.data.totalFailed ?? 0,
      results: response.data.results.map((roleData: any) => ({
        ...roleData,
        date: ConvertUTCtoDate(roleData.createdAt),
        time: FormatUtcString(ConvertUTCtoHHMM(roleData.createdAt)),
        id: roleData._id,
      })),
    };
  },
);


// a thunk to fetch devices filters
export const fetchListPaymentMethodFilters = createAsyncThunk<
  CommonFilterProperties[], // Return type
  void // No need for an argument, since we are not using the payload
>('commercial-sessions-management/fetchListPaymentMethodFilters', async (_, { dispatch }) => {
  try {
    const response = await transactionApi.fetchCommercialListFilters();
    const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
    return updatedResponse;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: TransactionEMSPInitialProperties = {
  payments: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: null,
  sortField: null,
  TotalFinising: 0,
  totalInprocess: 0,
  totalFailed: 0,
};

// Create the slice
export const paymentSlice = createSlice({
  name: 'commercial-sessions-management',
  initialState,
  reducers: {
    /**
     * Update device management filters
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    updatePaymentManagementFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
        state.sortBy = null;
        state.sortField = null;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param {any} state - The current state.
     */
    disablePaymentManagementApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * function for set sortBy
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param {any} builder - The builder object.
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllSession.pending, (state: TransactionEMSPInitialProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchAllSession.fulfilled,
        (state: TransactionEMSPInitialProperties, action: { payload: APIResponseInterface }) => {
          state.loading = false;
          state.payments = [...action.payload.results];
          state.limit = action.payload.limit ?? state.limit;
          state.page = action.payload.page ?? (action as any).meta?.arg?.page ?? state.page ?? 1;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
          state.TotalFinising = action.payload.TotalFinising;
          state.totalInprocess = action.payload.totalInprocess;
          state.totalFailed = action.payload.totalFailed;
        },
      )
      .addCase(fetchAllSession.rejected, (state: TransactionEMSPInitialProperties) => {
        state.loading = false;
        // Handle error if needed
      });
    builder.addCase(refreshAllSessions.fulfilled, (state, action) => {
      state.payments = [...action.payload.results];
      state.limit = action.payload.limit ?? state.limit;
      state.page = action.payload.page ?? (action as any).meta?.arg?.page ?? state.page ?? 1;
      state.totalPages = action.payload.totalPages;
      state.totalResults = action.payload.totalResults;
      state.TotalFinising = action.payload.TotalFinising;
      state.totalInprocess = action.payload.totalInprocess;
      state.totalFailed = action.payload.totalFailed;
    });
    builder
      .addCase(fetchListPaymentMethodFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchListPaymentMethodFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload].filter(
            (object, index, self) => index === self.findIndex((t) => t.key === object.key),
          );
        },
      )
      .addCase(fetchListPaymentMethodFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export the actions
export const { updatePaymentManagementFilters, disablePaymentManagementApplied, setSortData } =
  paymentSlice.actions;
export default paymentSlice.reducer;
